import { useState, useEffect, useMemo } from "react";
import { API } from "aws-amplify";
import {
  Box,
  Tabs,
  Tab,
  Text,
  Card,
  CardHeader,
  CardBody,
  Button,
  Spinner,
  TextInput,
} from "grommet";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { getTocicoData } from "../../graphql/queries";

dayjs.extend(utc);
dayjs.extend(timezone);

const CENTRAL_TZ = "America/Chicago";
const EXAM_CODE_FONT_FAMILY =
  'source-code-pro, Menlo, Monaco, Consolas, "Courier New", monospace';

function displayExamCode(code) {
  return String(code).toUpperCase();
}

function formatExamDateTime(examDateTime) {
  if (!examDateTime) return "Not provided";
  return dayjs.utc(examDateTime).tz(CENTRAL_TZ).format("MM/DD/YYYY hh:mm A z");
}

function splitName(name) {
  if (!name?.trim()) return { firstName: "", lastName: "" };
  const parts = name.trim().split(/\s+/);
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" "),
  };
}

function getStoredExamCode(exam) {
  if (!exam?.fastTestData) return null;

  try {
    const data =
      typeof exam.fastTestData === "string"
        ? JSON.parse(exam.fastTestData)
        : exam.fastTestData;

    if (data?.testCode || data?.testURI) {
      return { testCode: data.testCode, testURI: data.testURI };
    }
  } catch (error) {
    console.error("Failed to parse fastTestData:", error);
  }

  return null;
}

function ExamCard({ exam, setExamCode }) {
  const storedExamCode = getStoredExamCode(exam);
  const [testCode, setTestCode] = useState(storedExamCode?.testCode || null);
  const [testURI, setTestURI] = useState(storedExamCode?.testURI || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const stored = getStoredExamCode(exam);
    if (stored) {
      setTestCode(stored.testCode || null);
      setTestURI(stored.testURI || null);
    }
  }, [exam]);

  const handleGetExamCode = async () => {
    const { firstName, lastName } = splitName(exam.name);

    if (!firstName || !exam.email || !exam.id || !exam.examId) {
      setError("Name, email, ID, and exam ID are required to get an exam code.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await API.graphql({
        query: getTocicoData,
        variables: {
          action: "get_exam_code",
          payload: JSON.stringify({
            id: exam.id,
            firstName,
            lastName,
            email: exam.email,
            examId: exam.examId,
          }),
        },
      });

      const parsed = JSON.parse(response.data.getTocicoData);

      if (parsed?.error) {
        throw new Error(parsed.error);
      }

      setTestCode(parsed.testCode);
      setTestURI(parsed.testURI);
      setExamCode?.({
        testCode: parsed.testCode,
        testURI: parsed.testURI,
        id: exam.id,
      });
    } catch (err) {
      console.error("Error getting exam code:", err);
      setError(err.message || "Failed to get exam code.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card pad="small" margin={{ bottom: "small" }} height={{ min: "small" }}>
      <CardHeader align="center" justify="center">
        <Text size="large" weight="bold" textAlign="center">
          {exam.name || "Unknown"}
        </Text>
      </CardHeader>
      <CardBody pad={{ horizontal: "medium" }} gap="small">
        <Box direction="row" gap="small" justify="between">
          <Text weight="bold">Email:</Text>
          <Text>{exam.email || "Not provided"}</Text>
        </Box>
        <Box direction="row" gap="small" justify="between">
          <Text weight="bold">Exam Name:</Text>
          <Text>{exam.examName || "Not provided"}</Text>
        </Box>
        <Box direction="row" gap="small" justify="between">
          <Text weight="bold">Exam Date/Time:</Text>
          <Text>{formatExamDateTime(exam.examDateTime)}</Text>
        </Box>
        <Box direction="row" gap="small" justify="center" margin={{ top: "small" }}>
          <Button
            label={loading ? "Getting Exam Code..." : "Get Exam Code"}
            onClick={handleGetExamCode}
            primary
            color="black"
            disabled={loading}
          />
        </Box>
        {error && (
          <Text color="status-critical" size="small" textAlign="center">
            {error}
          </Text>
        )}
        {(testCode || testURI) && (
          <Box gap="xsmall" margin={{ top: "small" }}>
            {testCode && (
              <Box direction="row" gap="small" justify="between" align="center">
                <Text weight="bold">Exam Code:</Text>
                <Text
                  style={{
                    fontFamily: EXAM_CODE_FONT_FAMILY,
                    fontVariantNumeric: "tabular-nums",
                    letterSpacing: "0.08em",
                  }}
                >
                  {displayExamCode(testCode)}
                </Text>
              </Box>
            )}
            {testURI && (
              <Box direction="row" gap="small" justify="between">
                <Text weight="bold">Test URI:</Text>
                <Text>{testURI}</Text>
              </Box>
            )}
          </Box>
        )}
      </CardBody>
    </Card>
  );
}

export default function Tocico({ id, onClose, setExamCode }) {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchExams = async () => {
      try {
        const response = await API.graphql({
          query: getTocicoData,
          variables: {
            action: "list_tocico_exams",
            payload: JSON.stringify({}),
          },
        });
        const parsed = JSON.parse(response.data.getTocicoData);
        if (Array.isArray(parsed)) {
          setExams(parsed);
        } else if (parsed?.error) {
          console.error("Error from getTocicoData:", parsed);
        }
      } catch (error) {
        console.error("Error fetching TOCICO exams:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchExams();
  }, []);

  const matchedExam = useMemo(() => {
    if (!id || !exams.length) return null;
    return exams.find((exam) => exam.id === id) || null;
  }, [exams, id]);

  const filteredExams = useMemo(() => {
    if (!searchTerm.trim()) return exams;

    const searchLower = searchTerm.toLowerCase();
    return exams.filter((exam) => {
      const searchableFields = [
        exam.name,
        exam.email,
        exam.examName,
        exam.examDateTime,
        exam.id,
        exam.tocicoId,
      ].filter(Boolean);

      return searchableFields.some((field) =>
        String(field).toLowerCase().includes(searchLower),
      );
    });
  }, [exams, searchTerm]);

  const handleSearch = () => {
    setSearchTerm(searchInput);
  };

  return (
    <Box width="large" pad="medium" overflow="auto">
      {onClose && (
        <>
          <Box
            direction="row"
            justify="between"
            align="center"
            margin={{ bottom: "small" }}
          >
            <Button
              icon={<span style={{ fontSize: "20px" }}>×</span>}
              onClick={onClose}
              plain
              hoverIndicator
              title="Close"
            />
            <Text size="large" weight="bold" textAlign="center">
              TOCICO Exams
            </Text>
            <Box width="40px" />
          </Box>
          <Text size="small" textAlign="center" color="black">
            Click outside of the sidebar to close
          </Text>
        </>
      )}
      {!onClose && (
        <Text
          size="large"
          weight="bold"
          textAlign="center"
          margin={{ bottom: "medium" }}
        >
          TOCICO Exams
        </Text>
      )}

      {loading ? (
        <Box align="center" pad="large">
          <Spinner size="medium" />
          <Text margin={{ top: "small" }}>Loading exams...</Text>
        </Box>
      ) : (
        <Tabs activeIndex={activeTab} onActive={setActiveTab}>
          <Tab title="Matched">
            <Box pad={{ top: "medium" }} overflow="auto">
              {id ? (
                matchedExam ? (
                  <ExamCard exam={matchedExam} setExamCode={setExamCode} />
                ) : (
                  <Text>No matching exam found for this session.</Text>
                )
              ) : (
                <Text>No ID provided for matching.</Text>
              )}
            </Box>
          </Tab>

          <Tab title="All Exams">
            <Box pad={{ top: "medium" }} overflow="auto">
              <Box margin={{ bottom: "medium" }}>
                <Box direction="row" gap="small" align="center">
                  <TextInput
                    placeholder="Search exams..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                  <Button label="Search" onClick={handleSearch} primary />
                </Box>
                <Text size="small" margin={{ top: "xsmall" }}>
                  Search by name, email, exam name, or ID
                </Text>
              </Box>
              <Text margin={{ bottom: "medium" }}>
                Showing {filteredExams.length} of {exams.length} exam(s)
              </Text>
              {filteredExams.length > 0 ? (
                filteredExams.map((exam) => (
                  <ExamCard
                    exam={exam}
                    key={exam.id}
                    setExamCode={setExamCode}
                  />
                ))
              ) : (
                <Text>No exams found.</Text>
              )}
            </Box>
          </Tab>
        </Tabs>
      )}
    </Box>
  );
}
